
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, uploadFile, mediaUrl } from "../../Utils";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../Context/AuthContext";
import { useConfig } from "../../Context/ConfigContext";

interface FormValues {
    branch_id: string;
    vehicle_id: string;
    fuel_date_time: Date;
    fuel_type: string;
    odometer_reading: string;
    amount: string;

    payment_slip: FileList;
    remarks?: string;
}

const formatDateTime = (date: Date) => {
    const pad = (n: number) => n < 10 ? '0' + n : n;
    return date.getFullYear() + '-' +
        pad(date.getMonth() + 1) + '-' +
        pad(date.getDate()) + ' ' +
        pad(date.getHours()) + ':' +
        pad(date.getMinutes()) + ':' +
        pad(date.getSeconds());
};

export default function EditFuelExpense() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [docPreview, setDocPreview] = useState<string | null>(null);
    const { hasPermission } = useAuth();
    const { config } = useConfig();
    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<FormValues>();

    const selectedBranch = watch("branch_id");

    // Fetch Branches
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const data: any = await apiPost("branches/list", { status: 'Active' });
                setBranches(data);
            } catch (error) {
                console.error(error);
            }
        };
        fetchBranches();
    }, []);

    // Fetch Vehicles
    useEffect(() => {
        const fetchVehicles = async () => {
            if (!selectedBranch) {
                setVehicles([]);
                return;
            }
            try {
                const response: any = await apiPost("vehicles/list", { branch_id: selectedBranch });
                if (Array.isArray(response)) {
                    const activeVehicles = response.filter((v: any) => v.status !== 'Sold');
                    setVehicles(activeVehicles);
                } else if (response.data) {
                    const activeVehicles = Array.isArray(response.data) ? response.data.filter((v: any) => v.status !== 'Sold') : [];
                    setVehicles(activeVehicles);
                } else {
                    setVehicles([]);
                }
            } catch (error) {
                console.error(error);
                setVehicles([]);
            }
        };
        fetchVehicles();
    }, [selectedBranch]);

    // Fetch Data
    useEffect(() => {
        const fetchData = async () => {
            if (!location.state || !location.state.id) {
                toast.error("No Record ID found");
                navigate(`${process.env.PUBLIC_URL}/fuel_expense`);
                return;
            }

            try {
                const result = await apiPost("fuel_expenses/viewbyid", { id: location.state.id });

                setValue("branch_id", result.branch_id);

                // Pre-fetch vehicles for this branch so the vehicle dropdown is populated
                const vehiclesData: any = await apiPost("vehicles/list", { branch_id: result.branch_id });
                if (Array.isArray(vehiclesData)) setVehicles(vehiclesData);
                else if (vehiclesData.data) setVehicles(vehiclesData.data);

                setValue("vehicle_id", result.vehicle_id);
                setValue("fuel_date_time", new Date(result.fuel_date_time));
                setValue("fuel_type", result.fuel_type);
                setValue("odometer_reading", result.odometer_reading);
                setValue("amount", result.amount);

                setValue("remarks", result.remarks);

                if (result.payment_slip) {
                    setDocPreview(`${mediaUrl}${result.payment_slip}`);
                }

                setLoading(false);
            } catch (error) {
                console.error(error);
                toast.error("Failed to fetch details");
                setLoading(false);
            }
        };
        fetchData();
    }, [location.state, navigate, setValue]);

    const onSubmit = async (data: FormValues) => {
        setLoading(true);
        try {
            let slipName = "";
            if (data.payment_slip && data.payment_slip.length > 0) {
                const file = data.payment_slip[0];
                if (file.size > 2 * 1024 * 1024) {
                    toast.error("File size must be less than 2MB");
                    setLoading(false);
                    return;
                }
                slipName = await uploadFile(file);
            }

            // Create payload in a way that handles Dates correctly
            const payload: any = {
                id: location.state.id,
                ...data,
                fuel_date_time: formatDateTime(data.fuel_date_time),
            };

            if (slipName) {
                payload.payment_slip = slipName;
            } else {
                delete payload.payment_slip; // Don't wipe existing unless new one provided
            }
            if (payload.payment_slip instanceof FileList) delete payload.payment_slip;

            await apiPost("fuel_expenses/update", payload);
            toast.success("Fuel Expense updated successfully", { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/fuel_expense`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error updating fuel expense");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Edit Fuel Expense" parent="Operations" title="Edit Fuel Expense" />
            {loading ? <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '70vh' }}><Spinner color="primary" /></div> : (
                <Container fluid>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <H5>Expense Details</H5>
                                <hr />
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Label>Branch <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="branch_id"
                                            control={control}
                                            rules={{ required: "Branch is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.branch_id}>
                                                        <option value="">Select Branch</option>
                                                        {branches.map((b: any) => (
                                                            <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                        ))}
                                                    </Input>
                                                    <FormFeedback>{errors.branch_id?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Vehicle <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="vehicle_id"
                                            control={control}
                                            rules={{ required: "Vehicle is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.vehicle_id}>
                                                        <option value="">Select Vehicle</option>
                                                        {vehicles.map((v: any) => (
                                                            <option key={v.id} value={v.id}>{v.registration_number} ({v.brand} {v.model})</option>
                                                        ))}
                                                    </Input>
                                                    <FormFeedback>{errors.vehicle_id?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Fuel Date & Time <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="fuel_date_time"
                                            control={control}
                                            rules={{ required: "Date & Time is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <DatePicker
                                                        className={`form-control ${errors.fuel_date_time ? "is-invalid" : ""}`}
                                                        selected={field.value}
                                                        onChange={(date) => field.onChange(date)}
                                                        showTimeSelect
                                                        dateFormat="Pp"
                                                    />
                                                    <FormFeedback className="d-block">{errors.fuel_date_time?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <Label>Fuel Type <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="fuel_type"
                                            control={control}
                                            rules={{ required: "Fuel Type is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} placeholder="Petrol / Diesel" invalid={!!errors.fuel_type} autoComplete="off" />
                                                    <FormFeedback>{errors.fuel_type?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Label>Odometer Reading <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="odometer_reading"
                                            control={control}
                                            rules={{ required: "Odometer is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="number" {...field} placeholder="Reading" invalid={!!errors.odometer_reading} autoComplete="off" />
                                                    <FormFeedback>{errors.odometer_reading?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>


                                    <Col md={6}>
                                        <Label>Total Amount ({config.currency}) <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="amount"
                                            control={control}
                                            rules={{ required: "Amount is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="number" step="0.01" {...field} placeholder={`Amount (${config.currency})`} invalid={!!errors.amount} autoComplete="off" />
                                                    <FormFeedback>{errors.amount?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Label>Payment Slip </Label>
                                        <Controller
                                            name="payment_slip"
                                            control={control}
                                            render={({ field: { value, onChange, ...field } }) => (
                                                <>
                                                    <Input
                                                        type="file"
                                                        {...field}
                                                        accept="image/*,.pdf"
                                                        onChange={(e) => {
                                                            if (e.target.files && e.target.files.length > 0) {
                                                                const file = e.target.files[0];
                                                                if (file.size > 2 * 1024 * 1024) {
                                                                    toast.error("File size must be less than 2MB");
                                                                    e.target.value = "";
                                                                    onChange(null);
                                                                    return;
                                                                }
                                                            }
                                                            onChange(e.target.files);
                                                        }}
                                                    />
                                                    {docPreview && !(value instanceof FileList && value.length > 0) && (
                                                        <a href={docPreview} target="_blank" rel="noreferrer" className="d-block mt-2">View Current Slip</a>
                                                    )}
                                                    {value instanceof FileList && value.length > 0 && (
                                                        <a href={URL.createObjectURL(value[0])} target="_blank" rel="noreferrer" className="d-block mt-2">View New Slip</a>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={12}>
                                        <Label>Remarks</Label>
                                        <Controller
                                            name="remarks"
                                            control={control}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="textarea" {...field} placeholder="Any remarks..." autoComplete="off" />
                                                </>
                                            )}
                                        />
                                    </Col>
                                </Row>
                                {hasPermission('fuel-expense.edit') && (
                                    <div className="mt-4">
                                        <Btn color="primary" disabled={loading} type="submit">
                                            {loading ? <>Updating <Spinner size="sm" /></> : "Update"}
                                        </Btn>
                                    </div>
                                )}
                            </Form>
                        </CardBody>
                    </Card>
                </Container>
            )}
            <ToastContainer />
        </div>
    );
}
